import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { getProcessesByCvfRef } from "../../../../services/processService";
import { FormHandles } from "@unform/core";
import { FaCheck } from "react-icons/fa";
import InputField from "../../../../components/InputField";
import { companyOptions } from "../../../../contexts/CompanyContext";

type ProcessRefMatch = {
  id: string;
  cvfRef: string;
  cvfSubRef?: string;
  type?: number;
  clientName?: string;
};

const branchLabel = (type?: number) =>
  companyOptions.find((c) => c.value === type)?.label ?? "Sem filial";

const ProcessInput = ({
  formRef,
  processId,
  setProcessId,
  width,
}: {
  formRef?: RefObject<FormHandles>;
  processId: string | undefined;
  setProcessId: Dispatch<SetStateAction<string | undefined>>;
  width?: string;
}) => {
  const [cvfRef, setCvfRef] = useState("");
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  // O mesmo número existe em filiais diferentes: com mais de um candidato, o usuário escolhe.
  const [matches, setMatches] = useState<ProcessRefMatch[]>([]);

  const selectProcess = (id: string) => {
    setProcessId(id);
    if (formRef) {
      formRef.current?.setFieldValue("processId", id);
    }
  };

  const clearProcess = () => {
    setProcessId(undefined);
    if (formRef) {
      formRef.current?.setFieldValue("processId", undefined);
    }
  };

  const getProcessId = async () => {
    try {
      setError(undefined);
      setLoading(true);
      const response: ProcessRefMatch[] = await getProcessesByCvfRef(cvfRef);

      const found = response ?? [];
      setMatches(found);

      if (found.length === 1) {
        selectProcess(found[0].id);
      } else {
        clearProcess();
        if (found.length === 0) {
          setError("Processo não encontrado");
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cvfRef.length >= 3) {
      getProcessId();
    }
  }, [cvfRef]);

  return (
    <FormControl>
      <Box w={width ?? "60%"} mt={10}>
        <FormLabel>Nº do Processo: </FormLabel>
        <Flex alignItems="center" gap="24px">
          {formRef ? (
            <InputField
              name="processId"
              value={cvfRef}
              onChange={(e) => {
                setError(undefined);
                setMatches([]);
                clearProcess();
                setCvfRef(e.target.value);
                formRef.current?.setErrors({
                  ...formRef.current.getErrors(),
                  processId: "",
                });
              }}
            />
          ) : (
            <Input
              value={cvfRef}
              onChange={(e) => {
                setError(undefined);
                setMatches([]);
                clearProcess();
                setCvfRef(e.target.value);
              }}
            />
          )}
          {loading && <Spinner size="sm" color="var(--icon-color)" />}
        </Flex>
        {!processId && matches.length > 1 && (
          <Box mt="8px">
            <Text fontSize="14px" mb="4px">
              Esse número existe em mais de uma filial. Escolha:
            </Text>
            <Flex gap="8px" flexWrap="wrap">
              {matches.map((m) => (
                <Button
                  key={m.id}
                  size="sm"
                  variant="outline"
                  onClick={() => selectProcess(m.id)}
                >
                  {`${m.cvfRef}${m.cvfSubRef ?? ""} — ${branchLabel(m.type)}${
                    m.clientName ? ` — ${m.clientName}` : ""
                  }`}
                </Button>
              ))}
            </Flex>
          </Box>
        )}
        {processId ? (
          <Flex
            color="green.300"
            alignItems="center"
            gap="4px"
            fontSize="16px"
            mt="4px"
          >
            <FaCheck />
            <Text>
              {matches.length > 1
                ? `Processo selecionado: ${branchLabel(
                    matches.find((m) => m.id === processId)?.type
                  )}`
                : "Processo selecionado com sucesso"}
            </Text>
          </Flex>
        ) : error ? (
          <Text color="red.300">{error}</Text>
        ) : (
          ""
        )}
      </Box>
    </FormControl>
  );
};

export default ProcessInput;
